if (!customElements.get("td-combined-listing")) {
  let pendingRequests = new Map();
  let prefetchRequests = new Set();
  let prefetchCache = new Map();
  let ongoingFetches = new Set();
  
  /**
   * TdCombinedListing - Handles in-page product replacements for combined listings.
   * Implements eager prefetching strategy for instant content switching.
   */
  class TdCombinedListing extends HTMLElement {
    constructor() {
      super();
      this.products = this.querySelectorAll('[data-product-handle]');
      this.isDataSavingMode = navigator?.connection?.saveData;
      this.fetchSectionsTimeout = null;
      this.prefetchSectionsTimeout = null;
      this.slideChangeTimeout = null;
      this.hasRetried = false;
      this.sections = document.querySelectorAll('[data-combined-listing]');
    }

    connectedCallback() {
      if (this.products) {
        this.setupProductListeners();
        this.setupSlideChangeListener();
        this.scheduleInitialPrefetch();

        window.addEventListener('popstate', function() {
          if (history.state && history.state.url === location.href) {
            location.reload();
          }
        });
      }
    }

    /**
     * Sets up click and hover listeners on product items.
     */
    setupProductListeners() {
      this.products.forEach(prod => {
        prod.addEventListener('mouseover', () => {
          const productHandle = prod.dataset.productHandle;
          const variantId = prod.dataset.variantId;
          this.sections.forEach((section) => {
            const section_id = section.dataset.sectionId;
            this.prefetchSection(section_id, `/products/${productHandle}`, variantId);
          });
        });

        prod.addEventListener('click', () => {
          const productHandle = prod.dataset.productHandle;
          const variantId = prod.dataset.variantId;
          
          // Prevent change if this product/variant is already selected
          const currentHandle = this.getCurrentProductHandle();
          const currentVariantId = this.getCurrentVariantId();
          
          if (productHandle === currentHandle && variantId === currentVariantId) {
            return;
          }
          
          this.updateUrl(productHandle, variantId);
          this.sections.forEach((section) => {
            const section_id = section.dataset.sectionId;
            this.loadSection(section_id, `/products/${productHandle}`, variantId).then(
              (content) => {
                const sectionElement = document.querySelector(`[data-section-id='${section_id}']`);
                this.crossfadeSection(sectionElement, content, section_id);
              }
            );
          });
        });
      });
    }

    /**
     * Listens for slide-changed events from the Swiper slider to prefetch upcoming products.
     */
    setupSlideChangeListener() {
      const swiperSlider = this.querySelector('swiper-slider');
      if (!swiperSlider) return;

      swiperSlider.addEventListener('slide-changed', () => {
        this.handleSlideChange();
      });
    }

    /**
     * Handles slide change events with debouncing for rapid swipes.
     */
    handleSlideChange() {
      if (this.slideChangeTimeout) {
        clearTimeout(this.slideChangeTimeout);
      }
      this.slideChangeTimeout = setTimeout(() => {
        this.prefetchLookahead();
      }, 50);
    }

    /**
     * Schedules initial prefetch using requestIdleCallback or fallback.
     */
    scheduleInitialPrefetch() {
      const prefetch = () => this.prefetchVisibleProducts();

      if ('requestIdleCallback' in window) {
        requestIdleCallback(prefetch, { timeout: 1000 });
      } else {
        setTimeout(prefetch, 100);
      }
    }

    /**
     * Prefetches products visible in the slider viewport.
     * Falls back to products around the current product if Swiper is not ready.
     */
    prefetchVisibleProducts() {
      const swiperSlider = this.querySelector('swiper-slider');
      const slider = swiperSlider?.slider;

      let productsToFetch;

      if (slider?.visibleSlides && slider.visibleSlides.length > 0) {
        productsToFetch = Array.from(slider.visibleSlides);
      } else {
        // Fallback: find current product and prefetch products around it
        productsToFetch = this.getProductsAroundCurrent(5);
      }

      const currentHandle = this.getCurrentProductHandle();
      productsToFetch = productsToFetch.filter(
        prod => prod.dataset.productHandle !== currentHandle
      );

      this.prefetchProducts(productsToFetch);
    }

    /**
     * Gets products around the current product when Swiper isn't ready.
     * @param {number} count - Total number of products to return
     * @returns {HTMLElement[]} Array of product elements around the current one
     */
    getProductsAroundCurrent(count) {
      const allProducts = Array.from(this.products);
      const currentSlide = this.querySelector('.td-product__option--current');
      
      if (!currentSlide) {
        // No current product found, return first N products
        return allProducts.slice(0, count);
      }

      const currentIndex = allProducts.indexOf(currentSlide);
      if (currentIndex === -1) {
        return allProducts.slice(0, count);
      }

      // Get products before and after current, prioritizing after (scroll direction)
      const beforeCount = Math.floor(count / 3); // ~1-2 before
      const afterCount = count - beforeCount;    // ~3-4 after

      const startIndex = Math.max(0, currentIndex - beforeCount);
      const endIndex = Math.min(allProducts.length, currentIndex + afterCount + 1);

      return allProducts.slice(startIndex, endIndex);
    }

    /**
     * Prefetches products around the current slide position.
     * Prioritizes forward direction (3 ahead) but also prefetches 1 behind.
     */
    prefetchLookahead() {
      const swiperSlider = this.querySelector('swiper-slider');
      const slider = swiperSlider?.slider;
      if (!slider) return;

      const activeIndex = slider.activeIndex;
      const slides = Array.from(slider.slides);
      const currentHandle = this.getCurrentProductHandle();

      const lookaheadProducts = [];

      // Prefetch 3 products ahead
      for (let i = 1; i <= 3; i++) {
        const index = activeIndex + i;
        if (index < slides.length) {
          const slide = slides[index];
          if (slide.dataset.productHandle && slide.dataset.productHandle !== currentHandle) {
            lookaheadProducts.push(slide);
          }
        }
      }

      // Prefetch 1 product behind (for backward scrolling)
      const behindIndex = activeIndex - 1;
      if (behindIndex >= 0) {
        const slide = slides[behindIndex];
        if (slide.dataset.productHandle && slide.dataset.productHandle !== currentHandle) {
          lookaheadProducts.push(slide);
        }
      }

      this.prefetchProducts(lookaheadProducts);
    }

    /**
     * Prefetches sections and featured images for an array of product elements.
     * @param {HTMLElement[]} products - Array of product slide elements
     */
    prefetchProducts(products) {
      products.forEach(prod => {
        const productHandle = prod.dataset.productHandle;
        const variantId = prod.dataset.variantId;
        const featuredImage = prod.dataset.featuredImage;

        // Prefetch sections
        this.sections.forEach((section) => {
          const section_id = section.dataset.sectionId;
          this.prefetchSection(section_id, `/products/${productHandle}`, variantId);
        });

        // Prefetch featured image
        if (featuredImage) {
          this.prefetchImage(featuredImage);
        }
      });
    }

    /**
     * Prefetches an image by creating a new Image object.
     * Uses a cache to avoid duplicate prefetch requests.
     * @param {string} imageUrl - The URL of the image to prefetch
     */
    prefetchImage(imageUrl) {
      if (this.isDataSavingMode || !imageUrl) {
        return;
      }

      // Use module-scoped cache for images
      if (!this.imagePrefetchCache) {
        this.imagePrefetchCache = new Set();
      }

      if (this.imagePrefetchCache.has(imageUrl)) {
        return;
      }

      this.imagePrefetchCache.add(imageUrl);
      const img = new Image();
      img.src = imageUrl;
    }

    /**
     * Gets the current product handle from the URL.
     * @returns {string|null} The current product handle or null
     */
    getCurrentProductHandle() {
      const path = window.location.pathname;
      const match = path.match(/\/products\/([^/?]+)/);
      return match ? match[1] : null;
    }

    /**
     * Gets the current variant ID from the URL query parameters.
     * @returns {string|null} The current variant ID or null
     */
    getCurrentVariantId() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('variant') || null;
    }
    
    buildCacheKey(sectionId, path, variantId) {
      return `${path}:${sectionId}${variantId ? `:variant:${variantId}` : ''}`;
    }
    
    buildRequestGroups(sectionKeys) {
      const groups = sectionKeys.reduce(
        (acc, key) => {
          const [path, sectionId, , variantId] = key.split(':')
          const groupKey = `${path || ''}:${variantId || ''}`
    
          if (!acc[groupKey]) {
            acc[groupKey] = {
              path,
              variantId,
              keys: [],
              sectionIds: [],
            }
          }
          acc[groupKey].keys.push(key)
          acc[groupKey].sectionIds.push(sectionId)
          return acc
        },
        {}
      )
    
      return Object.values(groups)
    }
    
    scheduleFetchSections() {
      if (this.fetchSectionsTimeout) {
        clearTimeout(this.fetchSectionsTimeout)
      }
      this.fetchSectionsTimeout = setTimeout(() => this.fetchSections(), 100)
    }
    
    schedulePrefetchSections() {
      if (this.prefetchSectionsTimeout) {
        clearTimeout(this.prefetchSectionsTimeout);
      }
      this.prefetchSectionsTimeout = setTimeout(() => this.prefetchSections(), 100);
    }
    
    async fetchSections() {
      if (pendingRequests.size === 0) {
        return
      }
    
      const sectionKeys = Array.from(pendingRequests.keys())
      const requestGroups = this.buildRequestGroups(sectionKeys)
    
      try {
        await Promise.all(
          requestGroups.map(async ({ path, variantId, keys, sectionIds }) => {
            const url = new URL(window.location.href)
            if (path) {
              url.pathname = path
            }
            url.searchParams.set('sections', sectionIds.join(','))
            if (variantId) {
              url.searchParams.set('variant', variantId)
            }
            const requestUrl = url.toString()
    
            const response = await fetch(requestUrl)
            if (!response.ok) {
              throw new Error(`Failed to fetch: ${requestUrl}`)
            }
            const sectionsData = await response.json()
    
            keys.forEach((key, index) => {
              const callbacks = pendingRequests.get(key) || []
              pendingRequests.delete(key)
              const sectionId = sectionIds[index]
              const data = sectionsData[sectionId]
              if (data === undefined) {
                callbacks.forEach(({ reject }) => reject(new Error(`No data for section ${sectionId}`)))
              } else {
                callbacks.forEach(({ resolve }) => resolve(data))
                prefetchCache.set(key, data)
              }
              ongoingFetches.delete(key)
            })
          }),
        )
      } catch (error) {
        console.error(error)
        const sectionKeys = Array.from(pendingRequests.keys())
        sectionKeys.forEach((key) => {
          const callbacks = pendingRequests.get(key) || []
          pendingRequests.delete(key)
          callbacks.forEach(({ reject }) => reject(error))
          ongoingFetches.delete(key)
        })
      }
    }
    
    async prefetchSections() {
      if (prefetchRequests.size === 0) {
        return
      }
    
      const sectionKeys = Array.from(prefetchRequests)
      const requestGroups = this.buildRequestGroups(sectionKeys)
    
      try {
        await Promise.all(
          requestGroups.map(async ({ path, variantId, keys, sectionIds }) => {
            const url = new URL(window.location.href)
            if (path) {
              url.pathname = path
            }
            url.searchParams.set('sections', sectionIds.join(','))
            if (variantId) {
              url.searchParams.set('variant', variantId)
            }
            const requestUrl = url.toString()
    
            const response = await fetch(requestUrl)
            if (!response.ok) {
              throw new Error(`Failed to prefetch: ${requestUrl}`)
            }
            const sectionsData = await response.json()
    
            keys.forEach((key, index) => {
              const sectionId = sectionIds[index]
              const data = sectionsData[sectionId]
              if (data !== undefined) {
                prefetchCache.set(key, data)
              }
              prefetchRequests.delete(key)
              ongoingFetches.delete(key)
    
              const callbacks = pendingRequests.get(key) || []
              pendingRequests.delete(key)
              callbacks.forEach(({ resolve, reject }) => {
                if (data !== undefined) {
                  resolve(data)
                } else {
                  reject(new Error(`No data for section ${sectionId}`))
                }
              })
            })
          }),
        )
      } catch (error) {
        console.error(error);
        const failedKeys = Array.from(prefetchRequests);
        
        failedKeys.forEach((key) => {
          prefetchRequests.delete(key);
          ongoingFetches.delete(key);
          const callbacks = pendingRequests.get(key) || [];
          pendingRequests.delete(key);
          callbacks.forEach(({ reject }) => reject(error));
        });

        // Retry failed prefetches once after 1 second
        if (failedKeys.length > 0 && !this.hasRetried) {
          this.hasRetried = true;
          setTimeout(() => {
            failedKeys.forEach(key => {
              if (!prefetchCache.has(key)) {
                prefetchRequests.add(key);
              }
            });
            if (prefetchRequests.size > 0) {
              this.schedulePrefetchSections();
            }
            this.hasRetried = false;
          }, 1000);
        }
      }
    }
    
    loadSection(sectionId, path = '', variantId) {
      const key = this.buildCacheKey(sectionId, path, variantId)
    
      if (prefetchCache.has(key)) {
        return Promise.resolve(prefetchCache.get(key))
      }
    
      return new Promise((resolve, reject) => {
        if (!pendingRequests.has(key)) {
          pendingRequests.set(key, [])
        }
        pendingRequests.get(key)?.push({ resolve, reject })
    
        if (!ongoingFetches.has(key) && !prefetchRequests.has(key)) {
          ongoingFetches.add(key)
          this.scheduleFetchSections()
        }
      })
    }
    
    prefetchSection(sectionId, path = '', variantId) {
      if (this.isDataSavingMode) {
        return
      }
    
      const key = this.buildCacheKey(sectionId, path, variantId)
    
      if (pendingRequests.has(key) || ongoingFetches.has(key) || prefetchCache.has(key)) {
        return
      }
    
      if (!prefetchRequests.has(key)) {
        prefetchRequests.add(key)
        this.schedulePrefetchSections()
      }
    }
    
    cancelPrefetchSection(sectionId, path = '', variantId) {
      const key = this.buildCacheKey(sectionId, path, variantId)
      prefetchRequests.delete(key)
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    /**
     * Applies a smooth crossfade transition: fades out old content, replaces it, then fades in new content.
     * @param {HTMLElement} element - The section element to transition
     * @param {string} newContent - The new HTML content to display
     * @param {string} sectionId - The section ID for dispatching events
     */
    crossfadeSection(element, newContent, sectionId) {
      const transitionDuration = 200; // ms
      
      // Step 1: Fade out old content
      element.style.transition = `opacity ${transitionDuration}ms ease-in-out`;
      element.style.opacity = '0';
      
      // Step 2: Wait for fade out to complete, then replace content and fade in
      element.addEventListener('transitionend', () => {
        // Replace content while opacity is 0
        element.innerHTML = this.getSectionInnerHTML(newContent, `[data-section-id='${sectionId}']`);
        
        // Dispatch event after content is replaced
        element.dispatchEvent(new CustomEvent("section-content::changed", { bubbles: true, detail: sectionId }));
        
        // Step 3: Fade in new content
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            element.style.opacity = '1';
            
            // Clean up inline styles after transition completes
            element.addEventListener('transitionend', () => {
              element.style.transition = '';
              element.style.opacity = '';
            }, { once: true });
          });
        });
      }, { once: true });
    }

    updateUrl(productHandle, variantId) {
      let newUrl = `/products/${productHandle}`;
      const currentUrl = new URL(window.location.href);
      const currentParams = new URLSearchParams(currentUrl.search);
    
      currentParams.forEach((value, key) => {
        if (key === 'variant' || key === 'selling_plan') {
          currentParams.delete(key);
        }
      });

      if (variantId) {
        currentParams.set('variant', variantId);
      }
    
      const finalUrl = `${currentUrl.origin}${newUrl}?${currentParams.toString()}`;
    
      history.pushState({ url: finalUrl }, null, finalUrl);
    }
  }

  customElements.define("td-combined-listing", TdCombinedListing);
}

(function() {
  // Standalone function for main color management based on product images
  function initProductColorManager() {
    let defaultProductColor = null;
    const productContainer = document.querySelector('.td-product-main');
    
    if (productContainer && productContainer.hasAttribute('data-images-primary-color')) {
      defaultProductColor = getComputedStyle(productContainer).getPropertyValue('--product-color');
      document.addEventListener('slide-changed', (event) => {
        const { activeSlide, sliderId } = event.detail;
        updateProductColorFromSlide(activeSlide, sliderId);
      });
    }
    
    function updateProductColorFromSlide(activeSlide, sliderId) {
      if (!sliderId || !activeSlide) return;

      let slider = productContainer.querySelector(`#${sliderId}`);
      if (!slider) return;

      const mediaElement = activeSlide.querySelector('.td-product__media');
      if (!mediaElement) return;
      const mediaColor = mediaElement.dataset.mediaColor;
      updateProductColor(mediaColor);
    }
    
    function updateProductColor(mediaColor) {
      if (mediaColor) {
        productContainer.style.setProperty('--product-color', mediaColor);
      } else if (defaultProductColor) {
        productContainer.style.setProperty('--product-color', defaultProductColor);
      }
    }
  }
  initProductColorManager();
})();
